import { create } from 'zustand';
import { messageOf } from '@/api/miniapp/client';
import { miniappApi } from '@/api/miniapp/miniappApi';
import { useAuthStore } from './authStore';
import { useBackendCourseStore } from './backendCourseStore';
import { useBackendSurveyStore } from './backendSurveyStore';
import type { DoctorProfile } from '@/api/miniapp/types';

/**
 * Профессиональные профили врача на реальном бэкенде.
 *
 * Идентификатор числовой (`doctor_profile_id`), максимум три профиля.
 * Активный профиль определяет данные анкеты и курсов, поэтому после выбора
 * или создания вызывающий код обязан перезапросить вопросы и ответы.
 */
const MAX_PROFILES = 3;

/**
 * Данные, привязанные к активному профилю. После смены профиля они
 * недействительны: контракт требует заново запросить анкету, а курс и уроки
 * сервер отдаёт для активного профиля.
 */
function dropProfileScopedData(): void {
  useBackendSurveyStore.getState().reset();
  useBackendCourseStore.getState().reset();
}

/**
 * Смена и создание профиля меняют состояние сессии: новый профиль требует
 * заполненной анкеты, и до её завершения бэкенд отвечает `REGISTRATION_REQUIRED`
 * на курс и уроки. Поэтому после операции сессия перечитывается, и приложение
 * идёт туда, куда указывает сервер, а не на курс по умолчанию.
 */
async function syncSessionState(): Promise<void> {
  await useAuthStore.getState().ping();
}

interface DoctorProfilesState {
  profiles: DoctorProfile[];
  activeId: number | null;
  loading: boolean;
  busy: boolean;
  error: string | null;

  load(): Promise<void>;
  select(doctorProfileId: number): Promise<boolean>;
  create(name: string, copyFromId?: number): Promise<DoctorProfile | null>;
}

/**
 * Достигнут ли предел в три профиля.
 *
 * Отдельная функция, а не геттер в объекте состояния: zustand вычисляет такой
 * геттер один раз при создании стора и дальше отдаёт уже посчитанное значение,
 * из-за чего ограничение не срабатывало.
 */
export function canCreateProfile(profiles: DoctorProfile[]): boolean {
  return profiles.length < MAX_PROFILES;
}

export const useDoctorProfilesStore = create<DoctorProfilesState>((set, get) => ({
  profiles: [],
  activeId: null,
  loading: false,
  busy: false,
  error: null,

  async load() {
    set({ loading: true, error: null });
    try {
      const response = await miniappApi.getProfiles();
      set({
        profiles: response.profiles ?? [],
        activeId: response.active_doctor_profile_id ?? null,
        loading: false,
      });
    } catch (error) {
      set({
        loading: false,
        error: messageOf(error, 'Не удалось загрузить профили'),
      });
    }
  },

  async select(doctorProfileId) {
    if (get().activeId === doctorProfileId) return true;

    set({ busy: true, error: null });
    try {
      await miniappApi.selectProfile(doctorProfileId);
      set({ busy: false, activeId: doctorProfileId });
      // Активный профиль определяет анкету и курс, поэтому их данные
      // сбрасываются: иначе на экране остались бы данные прежнего профиля
      dropProfileScopedData();
      await syncSessionState();
      return true;
    } catch (error) {
      set({ busy: false, error: messageOf(error, 'Не удалось выбрать профиль') });
      return false;
    }
  },

  async create(name, copyFromId) {
    if (!canCreateProfile(get().profiles)) {
      set({ error: 'Достигнут лимит в три профиля' });
      return null;
    }
    set({ busy: true, error: null });
    try {
      const profile = await miniappApi.createProfile({
        profile_name: name.trim(),
        copy_from_doctor_profile_id: copyFromId,
      });
      // Созданный профиль становится активным; перечитываем список ради счётчика
      await get().load();
      set({ busy: false, activeId: profile.doctor_profile_id });
      dropProfileScopedData();
      await syncSessionState();
      return profile;
    } catch (error) {
      set({ busy: false, error: messageOf(error, 'Не удалось создать профиль') });
      return null;
    }
  },
}));
